import React from "react";
import {
  FaArrowRight,
  FaBell,
  FaCar,
  FaGift,
  FaPlane,
  FaTicketAlt,
  FaWifi,
} from "react-icons/fa";

import type { TravelCategory } from "../../config/travelpayoutsWidgets";

type Action = {
  id: string;
  icon: React.ComponentType;
  titleFr: string;
  titleEn: string;
  descriptionFr: string;
  descriptionEn: string;
  path: string;
  badgeFr: string;
  badgeEn: string;
};

type Props = {
  activeCategory: TravelCategory;
  isEn: boolean;
  onAction: (action: Action) => void;
  onAlert: () => void;
};

const actionsByCategory: Record<TravelCategory, Action[]> = {
  flights: [
    {
      id: "flight-to-car",
      icon: FaCar,
      titleFr: "Ajoutez une voiture",
      titleEn: "Add a rental car",
      descriptionFr:
        "Complétez votre arrivée avec une option de mobilité adaptée à votre séjour.",
      descriptionEn:
        "Complete your arrival with mobility suited to your stay.",
      path: "/comparateur-voyage?category=cars",
      badgeFr: "Étape suivante",
      badgeEn: "Next step",
    },
    {
      id: "flight-to-esim",
      icon: FaWifi,
      titleFr: "Préparez votre eSIM",
      titleEn: "Prepare your eSIM",
      descriptionFr:
        "Restez connecté dès l’atterrissage sans chercher une carte SIM sur place.",
      descriptionEn:
        "Stay connected as soon as you land without searching for a SIM card.",
      path: "/comparateur-voyage?category=esim",
      badgeFr: "Essentiel voyage",
      badgeEn: "Travel essential",
    },
    {
      id: "flight-to-local",
      icon: FaGift,
      titleFr: "Réservez une expérience locale",
      titleEn: "Book a local experience",
      descriptionFr:
        "Ajoutez une activité mémorable offerte dans l’écosystème Forfeo.",
      descriptionEn:
        "Add a memorable activity offered in the Forfeo ecosystem.",
      path: "/boutique?source=forfy_compare",
      badgeFr: "Choix Forfeo",
      badgeEn: "Forfeo choice",
    },
  ],
  cars: [
    {
      id: "car-to-flight",
      icon: FaPlane,
      titleFr: "Vérifiez votre vol",
      titleEn: "Check your flight",
      descriptionFr:
        "Comparez les options de départ avant de finaliser votre mobilité.",
      descriptionEn:
        "Compare departure options before finalizing your mobility.",
      path: "/comparateur-voyage?category=flights",
      badgeFr: "Comparer",
      badgeEn: "Compare",
    },
    {
      id: "car-to-local",
      icon: FaGift,
      titleFr: "Trouvez quoi faire sur place",
      titleEn: "Find what to do locally",
      descriptionFr:
        "Découvrez des expériences locales accessibles pendant votre escapade.",
      descriptionEn:
        "Discover local experiences available during your getaway.",
      path: "/boutique?source=forfy_compare",
      badgeFr: "Local",
      badgeEn: "Local",
    },
    {
      id: "car-to-lastcall",
      icon: FaTicketAlt,
      titleFr: "Voyez les occasions Last Call",
      titleEn: "See Last Call opportunities",
      descriptionFr:
        "Repérez les disponibilités et occasions de dernière minute.",
      descriptionEn:
        "Find last-minute availability and opportunities.",
      path: "/last-call?source=forfy_compare",
      badgeFr: "Dernière minute",
      badgeEn: "Last minute",
    },
  ],
  esim: [
    {
      id: "esim-to-flight",
      icon: FaPlane,
      titleFr: "Comparez votre vol",
      titleEn: "Compare your flight",
      descriptionFr:
        "Réunissez votre transport et votre connectivité dans le même parcours.",
      descriptionEn:
        "Bring transportation and connectivity together in one journey.",
      path: "/comparateur-voyage?category=flights",
      badgeFr: "Planifier",
      badgeEn: "Plan",
    },
    {
      id: "esim-to-experiences",
      icon: FaTicketAlt,
      titleFr: "Ajoutez un événement",
      titleEn: "Add an event",
      descriptionFr:
        "Cherchez des billets de concerts, sports et événements à destination.",
      descriptionEn:
        "Search for concert, sports and event tickets at your destination.",
      path: "/comparateur-voyage?category=experiences",
      badgeFr: "À découvrir",
      badgeEn: "Discover",
    },
    {
      id: "esim-to-local",
      icon: FaGift,
      titleFr: "Explorez Forfeo",
      titleEn: "Explore Forfeo",
      descriptionFr:
        "Complétez votre voyage avec une expérience locale sélectionnée.",
      descriptionEn:
        "Complete your trip with a selected local experience.",
      path: "/boutique?source=forfy_compare",
      badgeFr: "Expérience locale",
      badgeEn: "Local experience",
    },
  ],
  experiences: [
    {
      id: "events-to-flight",
      icon: FaPlane,
      titleFr: "Planifiez le déplacement",
      titleEn: "Plan the trip",
      descriptionFr:
        "Comparez les vols pour rejoindre votre événement plus facilement.",
      descriptionEn:
        "Compare flights to reach your event more easily.",
      path: "/comparateur-voyage?category=flights",
      badgeFr: "Transport",
      badgeEn: "Transport",
    },
    {
      id: "events-to-car",
      icon: FaCar,
      titleFr: "Ajoutez une voiture",
      titleEn: "Add a rental car",
      descriptionFr:
        "Organisez vos déplacements avant et après l’événement.",
      descriptionEn:
        "Organize your travel before and after the event.",
      path: "/comparateur-voyage?category=cars",
      badgeFr: "Mobilité",
      badgeEn: "Mobility",
    },
    {
      id: "events-to-local",
      icon: FaGift,
      titleFr: "Prolongez l’expérience",
      titleEn: "Extend the experience",
      descriptionFr:
        "Ajoutez une sortie locale Forfeo à votre programme.",
      descriptionEn: "Add a local Forfeo outing to your itinerary.",
      path: "/boutique?source=forfy_compare",
      badgeFr: "Forfeo local",
      badgeEn: "Forfeo local",
    },
  ],
};

export default function CommercialConversionPanel({
  activeCategory,
  isEn,
  onAction,
  onAlert,
}: Props) {
  const actions = actionsByCategory[activeCategory];

  return (
    <section className="mt-12 overflow-hidden rounded-[2.25rem] border border-slate-200 bg-slate-950 text-white shadow-2xl shadow-slate-900/15">
      <div className="grid gap-8 p-6 md:p-9 lg:grid-cols-[0.72fr_1.28fr]">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-300">
            {isEn ? "Forfy smart sequence" : "Parcours intelligent Forfy"}
          </p>
          <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">
            {isEn
              ? "Your search is only the first step."
              : "Votre recherche n’est que la première étape."}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 md:text-base">
            {isEn
              ? "Continue planning with the most useful next actions, or ask Forfy to watch for a better opportunity."
              : "Continuez votre planification avec les prochaines actions les plus utiles, ou demandez à Forfy de surveiller une meilleure occasion."}
          </p>

          <button
            type="button"
            onClick={onAlert}
            className="mt-6 inline-flex items-center gap-3 rounded-full bg-cyan-300 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:-translate-y-1 hover:bg-white"
          >
            <FaBell /> {isEn ? "Create a smart alert" : "Créer une alerte intelligente"}
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                type="button"
                key={action.id}
                onClick={() => onAction(action)}
                className="group rounded-[1.6rem] border border-white/10 bg-white/[0.07] p-5 text-left backdrop-blur transition hover:-translate-y-1 hover:border-cyan-300/50 hover:bg-white/[0.12]"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-300/10 text-cyan-300 transition group-hover:bg-cyan-300 group-hover:text-slate-950">
                    <Icon />
                  </span>
                  <span className="rounded-full bg-white/10 px-3 py-1 text-[9px] font-black uppercase tracking-wider text-slate-300">
                    {isEn ? action.badgeEn : action.badgeFr}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-black">
                  {isEn ? action.titleEn : action.titleFr}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  {isEn ? action.descriptionEn : action.descriptionFr}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-cyan-300">
                  {isEn ? "Continue" : "Continuer"} <FaArrowRight />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
